import { useEffect, useState, useRef, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Linkedin, MessageCircle, Link as LinkIcon, Check } from "lucide-react";
import axios from "axios";
import blogArticles from "@/data/blogArticles";
import ThreadedComments from "@/components/ThreadedComments";
import BlogCoverImage from "@/components/BlogCoverImage";
import { safeHtml } from "@/lib/sanitize";

const API = process.env.REACT_APP_BACKEND_URL;
const SITE = "https://www.roleiq.pro";

// ── helpers ──
function slugifyHeading(text) {
  return (text || "").toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").slice(0, 80);
}

function authorInitials(name) {
  const parts = (name || "").trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "RQ";
}

function formatDate(iso, fallback = "") {
  if (!iso) return fallback;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
  } catch { return fallback; }
}

/**
 * Convert markdown-ish article body into structured blocks with stable ids on H2s.
 * Supports: ## heading, ### subheading, **bold**, *italic*, > blockquote (highlight box),
 *           paragraphs (blank-line separated), and short-line H2 fallback.
 */
function parseBody(content) {
  if (!content) return { blocks: [], headings: [] };
  const escape = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s) => {
    let h = escape(s);
    h = h.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    h = h.replace(/(^|[^*])\*(?!\s)([^*]+?)\*/g, "$1<em>$2</em>");
    h = h.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    return h;
  };

  const blocks = [];
  const headings = [];
  const raw = content.split(/\n\n+/);
  raw.forEach((block, i) => {
    const trimmed = block.trim();
    if (!trimmed) return;
    if (trimmed.startsWith("## ")) {
      const text = trimmed.slice(3).trim();
      const id = slugifyHeading(text) || `section-${i}`;
      headings.push({ id, text });
      blocks.push({ type: "h2", id, html: inline(text) });
      return;
    }
    if (trimmed.startsWith("### ")) {
      blocks.push({ type: "h3", html: inline(trimmed.slice(4).trim()) });
      return;
    }
    if (trimmed.startsWith("> ")) {
      // Highlight box (single or multi-line blockquote)
      const cleaned = trimmed.replace(/^>\s?/gm, "");
      blocks.push({ type: "highlight", html: inline(cleaned) });
      return;
    }
    // Plain title-like short line (under 80 chars + no period at end) treated as h2
    if (trimmed.length < 80 && !trimmed.endsWith(".") && !trimmed.includes("\n")) {
      const id = slugifyHeading(trimmed) || `section-${i}`;
      headings.push({ id, text: trimmed });
      blocks.push({ type: "h2", id, html: inline(trimmed) });
      return;
    }
    // Paragraph (preserve single line breaks as <br/>)
    const para = inline(trimmed).replace(/\n/g, "<br/>");
    blocks.push({ type: "p", html: para });
  });
  return { blocks, headings };
}

// Imperative document.head manager for SEO meta + JSON-LD
function useArticleSEO(article) {
  useEffect(() => {
    if (!article) return undefined;
    const head = document.head;
    const created = [];

    const setMeta = (selector, attrs) => {
      let el = head.querySelector(selector);
      if (!el) {
        el = document.createElement(attrs.tag || "meta");
        created.push(el);
        head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => {
        if (k === "tag") return;
        if (v == null) return;
        el.setAttribute(k, v);
      });
      return el;
    };

    const tagsCsv = (article.tags || []).join(", ");
    const coverAbs = article.cover_image
      ? (article.cover_image.startsWith("http") ? article.cover_image : `${API}${article.cover_image}`)
      : `${SITE}/logo.png`;
    const canonical = `${SITE}/blog/${article.slug}`;
    const title = `${article.title} | RoleIQ Blog`;

    document.title = title;

    setMeta('meta[name="description"]', { tag: "meta", name: "description", content: article.meta_description || article.excerpt || "" });
    setMeta('meta[name="keywords"]', { tag: "meta", name: "keywords", content: tagsCsv });
    setMeta('meta[name="author"]', { tag: "meta", name: "author", content: article.author || "RoleIQ Team" });
    setMeta('meta[name="robots"]', { tag: "meta", name: "robots", content: "index, follow" });

    let canon = head.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement("link"); canon.setAttribute("rel", "canonical"); created.push(canon); head.appendChild(canon); }
    canon.setAttribute("href", canonical);

    // Open Graph
    setMeta('meta[property="og:type"]', { tag: "meta", property: "og:type", content: "article" });
    setMeta('meta[property="og:title"]', { tag: "meta", property: "og:title", content: article.title });
    setMeta('meta[property="og:description"]', { tag: "meta", property: "og:description", content: article.meta_description || article.excerpt || "" });
    setMeta('meta[property="og:image"]', { tag: "meta", property: "og:image", content: coverAbs });
    setMeta('meta[property="og:image:width"]', { tag: "meta", property: "og:image:width", content: "1200" });
    setMeta('meta[property="og:image:height"]', { tag: "meta", property: "og:image:height", content: "630" });
    setMeta('meta[property="og:url"]', { tag: "meta", property: "og:url", content: canonical });
    setMeta('meta[property="og:site_name"]', { tag: "meta", property: "og:site_name", content: "RoleIQ" });
    setMeta('meta[property="article:published_time"]', { tag: "meta", property: "article:published_time", content: article.published_at || article.created_at || "" });
    setMeta('meta[property="article:author"]', { tag: "meta", property: "article:author", content: article.author || "RoleIQ Team" });
    setMeta('meta[property="article:tag"]', { tag: "meta", property: "article:tag", content: tagsCsv });

    // Twitter
    setMeta('meta[name="twitter:card"]', { tag: "meta", name: "twitter:card", content: "summary_large_image" });
    setMeta('meta[name="twitter:title"]', { tag: "meta", name: "twitter:title", content: article.title });
    setMeta('meta[name="twitter:description"]', { tag: "meta", name: "twitter:description", content: article.meta_description || article.excerpt || "" });
    setMeta('meta[name="twitter:image"]', { tag: "meta", name: "twitter:image", content: coverAbs });

    // JSON-LD
    const wordCount = (article.content || "").split(/\s+/).filter(Boolean).length;
    const ld = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.meta_description || article.excerpt || "",
      image: coverAbs,
      author: { "@type": "Person", name: article.author || "RoleIQ Team", url: `${SITE}/blog` },
      publisher: { "@type": "Organization", name: "RoleIQ", logo: { "@type": "ImageObject", url: `${SITE}/logo.png` } },
      datePublished: article.published_at || article.created_at || "",
      dateModified: article.updated_at || article.published_at || article.created_at || "",
      mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
      keywords: tagsCsv,
      wordCount,
      articleSection: article.category || "HR Intelligence",
    };
    let ldEl = head.querySelector('script[data-roleiq-ld]');
    if (!ldEl) { ldEl = document.createElement("script"); ldEl.setAttribute("type", "application/ld+json"); ldEl.setAttribute("data-roleiq-ld", "1"); created.push(ldEl); head.appendChild(ldEl); }
    ldEl.textContent = JSON.stringify(ld);

    return () => {
      // Cleanup only nodes we created (so we don't strip pre-existing meta)
      created.forEach(n => n.parentNode && n.parentNode.removeChild(n));
      document.title = "RoleIQ — AI-Powered Intelligence";
    };
  }, [article]);
}

function InlineCTA() {
  return (
    <div className="inline-cta my-8 rounded-2xl p-6 sm:p-7 flex flex-wrap items-center justify-between gap-5" style={{ background: "#0F1E35" }} data-testid="inline-cta">
      <div className="flex-1 min-w-[220px]">
        <p className="font-dm font-semibold mb-1" style={{ color: "#C9A84C", fontSize: "17px" }}>Try 4D CapScan free on RoleIQ</p>
        <p className="font-dm text-white/80 text-sm leading-relaxed">Map competency gaps for your team in minutes. No setup required.</p>
      </div>
      <Link to="/capscan" className="font-dm font-semibold text-sm px-5 py-2.5 rounded-lg transition-opacity hover:opacity-90 whitespace-nowrap" style={{ background: "#C9A84C", color: "#0F1E35" }} data-testid="inline-cta-btn">
        Start free →
      </Link>
    </div>
  );
}

function ShareButtons({ url, title, variant = "small" }) {
  const [copied, setCopied] = useState(false);
  const enc = encodeURIComponent(url);
  const titleEnc = encodeURIComponent(title);
  const onCopy = () => {
    navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  if (variant === "bar") {
    return (
      <div className="flex items-center gap-3 flex-wrap" data-testid="share-bar-buttons">
        <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc}`} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-2 font-dm text-sm font-medium px-4 py-2 rounded-lg text-white hover:opacity-85 transition-opacity" style={{ background: "#0A66C2" }}>
          <Linkedin className="w-4 h-4" /> LinkedIn
        </a>
        <a href={`https://api.whatsapp.com/send?text=${titleEnc}%20${enc}`} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-2 font-dm text-sm font-medium px-4 py-2 rounded-lg text-white hover:opacity-85 transition-opacity" style={{ background: "#25D366" }}>
          <MessageCircle className="w-4 h-4" /> WhatsApp
        </a>
        <button onClick={onCopy} data-testid="share-copy-bar"
          className="flex items-center gap-2 font-dm text-sm font-medium px-4 py-2 rounded-lg border border-navy/15 bg-cream/50 text-navy hover:bg-navy/5 transition-colors">
          {copied ? <><Check className="w-4 h-4" /> Copied!</> : <><LinkIcon className="w-4 h-4" /> Copy link</>}
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2" data-testid="share-buttons-top">
      <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc}`} target="_blank" rel="noopener noreferrer" title="Share on LinkedIn"
        className="w-8 h-8 rounded-full border border-navy/15 bg-white text-navy/60 hover:border-navy hover:text-navy flex items-center justify-center transition-colors">
        <Linkedin className="w-4 h-4" />
      </a>
      <a href={`https://api.whatsapp.com/send?text=${titleEnc}%20${enc}`} target="_blank" rel="noopener noreferrer" title="Share on WhatsApp"
        className="w-8 h-8 rounded-full border border-navy/15 bg-white text-navy/60 hover:border-navy hover:text-navy flex items-center justify-center transition-colors">
        <MessageCircle className="w-4 h-4" />
      </a>
      <button onClick={onCopy} title="Copy link" data-testid="share-copy-top"
        className="w-8 h-8 rounded-full border border-navy/15 bg-white text-navy/60 hover:border-navy hover:text-navy flex items-center justify-center transition-colors">
        {copied ? <Check className="w-4 h-4" /> : <LinkIcon className="w-4 h-4" />}
      </button>
    </div>
  );
}

export default function BlogArticlePage() {
  const { slug } = useParams();
  const hardcoded = blogArticles.find(a => a.slug === slug);
  const [dbArticle, setDbArticle] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [related, setRelated] = useState([]);
  const [activeHeading, setActiveHeading] = useState("");

  useEffect(() => {
    setLoaded(false);
    axios.get(`${API}/api/blogs/${slug}`).then(r => setDbArticle(r.data)).catch(() => setDbArticle(null)).finally(() => setLoaded(true));
    axios.get(`${API}/api/blogs/${slug}/related`).then(r => setRelated(r.data || [])).catch(() => setRelated([]));
  }, [slug]);

  const article = dbArticle || hardcoded;

  const { blocks, headings } = useMemo(() => parseBody(article?.content || ""), [article]);
  const wordCount = useMemo(() => (article?.content || "").split(/\s+/).filter(Boolean).length, [article]);
  const readMins = Math.max(2, Math.ceil(wordCount / 200));

  useArticleSEO(article);

  // Active heading on scroll
  useEffect(() => {
    if (!headings.length) return undefined;
    const onScroll = () => {
      const scrollY = window.scrollY + 140;
      let current = headings[0]?.id;
      for (const h of headings) {
        const el = document.getElementById(h.id);
        if (el && el.offsetTop <= scrollY) current = h.id;
      }
      setActiveHeading(current || "");
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [headings]);

  if (!loaded && !hardcoded) {
    return <div className="bg-cream pt-28 pb-20 min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (!article) {
    return (
      <div className="bg-cream pt-28 pb-20 min-h-screen flex items-center justify-center">
        <div className="text-center"><h1 className="font-playfair text-navy text-3xl mb-4">Article Not Found</h1><Link to="/blog" className="text-gold hover:underline font-dm">Back to Blog</Link></div>
      </div>
    );
  }

  const fallbackRelated = blogArticles.filter(a => a.slug !== slug).slice(0, 3);
  const displayRelated = related.length > 0 ? related : fallbackRelated;
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const publishDateDisplay = formatDate(article.published_at || article.created_at, article.date || "");

  // Inject inline CTA after the 3rd H2 in the block list
  const renderedBlocks = [];
  let h2Count = 0;
  blocks.forEach((blk, idx) => {
    renderedBlocks.push({ ...blk, key: idx });
    if (blk.type === "h2") {
      h2Count += 1;
      // Find next non-H2 block index for context — we just insert after this entry's paragraph(s)
    }
  });
  // We need CTA after the section that follows the 3rd H2, i.e. after some paragraphs.
  // Strategy: find the 4th H2 (or end of blocks) — insert CTA right before it.
  let ctaInsertAt = -1;
  let h2seen = 0;
  for (let i = 0; i < renderedBlocks.length; i++) {
    if (renderedBlocks[i].type === "h2") {
      h2seen += 1;
      if (h2seen === 4) { ctaInsertAt = i; break; }
    }
  }
  if (ctaInsertAt === -1 && h2seen >= 3) {
    // Fewer than 4 H2s but ≥3 — insert at end
    ctaInsertAt = renderedBlocks.length;
  }

  return (
    <div className="bg-cream pt-24 pb-20" data-testid="blog-article-page">
      {/* Breadcrumb */}
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 pt-2 flex items-center gap-2 font-dm text-xs text-navy/40" data-testid="breadcrumb">
        <Link to="/" className="hover:text-gold">Home</Link>
        <span className="text-navy/30">›</span>
        <Link to="/blog" className="hover:text-gold">Blog</Link>
        <span className="text-navy/30">›</span>
        <span className="text-navy/60 line-clamp-1 max-w-[260px] sm:max-w-md">{article.title}</span>
      </div>

      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 mt-6 grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-12">
        {/* MAIN */}
        <main>
          <Link to="/blog" className="inline-flex items-center gap-2 font-dm text-gold text-sm mb-6 hover:underline" data-testid="back-to-blog">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>

          {/* Meta row */}
          <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="article-meta">
            <span className="font-dm text-[11px] font-medium tracking-[1.5px] uppercase px-3 py-1 rounded-full" style={{ background: "#E1F5EE", color: "#00837A" }}>{article.category}</span>
            <span className="w-1 h-1 rounded-full bg-navy/15" />
            <span className="font-dm text-xs text-navy/40">{publishDateDisplay}</span>
            <span className="w-1 h-1 rounded-full bg-navy/15" />
            <span className="font-dm text-xs text-navy/40 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {readMins} min read</span>
          </div>

          {/* Title */}
          <motion.h1 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="font-playfair text-navy text-3xl sm:text-4xl lg:text-5xl font-bold mb-5 leading-tight" data-testid="article-title">
            {article.title}
          </motion.h1>

          {/* Author + Share top */}
          <div className="flex items-center gap-3 py-4 border-y border-navy/10 mb-7" data-testid="author-row">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-dm text-sm font-semibold" style={{ background: "#0F1E35", color: "#C9A84C" }} data-testid="author-avatar">
              {authorInitials(article.author)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-dm text-sm font-medium text-navy">{article.author || "RoleIQ Team"}</p>
              <p className="font-dm text-xs text-navy/40">{article.author_role || "Editorial Team"}</p>
            </div>
            <ShareButtons url={shareUrl} title={article.title} />
          </div>

          {/* Cover */}
          <BlogCoverImage src={article.cover_image} alt={article.title} className="w-full aspect-[16/9] object-cover rounded-2xl mb-8 shadow-sm" placeholderClassName="rounded-2xl mb-8" />

          {/* Body */}
          <article className="article-body font-dm text-[16px] leading-[1.8] text-navy" data-testid="article-body">
            {renderedBlocks.map((blk, i) => {
              const node = (() => {
                if (blk.type === "h2") {
                  return <h2 key={`h2-${i}`} id={blk.id} className="font-playfair text-navy text-2xl sm:text-3xl font-semibold mt-10 mb-4 pt-2 scroll-mt-24" dangerouslySetInnerHTML={{ __html: safeHtml(blk.html) }} />;
                }
                if (blk.type === "h3") {
                  return <h3 key={`h3-${i}`} className="font-dm text-navy text-lg font-semibold mt-7 mb-2.5" dangerouslySetInnerHTML={{ __html: safeHtml(blk.html) }} />;
                }
                if (blk.type === "highlight") {
                  return (
                    <div key={`hl-${i}`} className="my-6 px-5 py-4 rounded-r-lg border-l-[3px]" style={{ background: "#E1F5EE", borderColor: "#00837A" }} data-testid="highlight-box">
                      <p className="font-dm font-medium text-navy m-0" dangerouslySetInnerHTML={{ __html: safeHtml(blk.html) }} />
                    </div>
                  );
                }
                return <p key={`p-${i}`} className="text-navy/85 mb-[18px]" dangerouslySetInnerHTML={{ __html: safeHtml(blk.html) }} />;
              })();
              if (i === ctaInsertAt) {
                return <div key={`wrap-${i}`}><InlineCTA /> {node}</div>;
              }
              return node;
            })}
            {ctaInsertAt === renderedBlocks.length && h2seen >= 3 && <InlineCTA />}
          </article>

          {/* Tags */}
          {(article.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-2 my-8 pt-6 border-t border-navy/10" data-testid="article-tags">
              {article.tags.map(t => (
                <Link key={t} to={`/blog?tag=${encodeURIComponent(t)}`} className="font-dm text-xs px-3.5 py-1.5 rounded-full bg-cream border border-navy/10 text-navy/60 hover:text-[#00837A] hover:border-[#00837A] transition-colors">{t}</Link>
              ))}
            </div>
          )}

          {/* Bottom share bar */}
          <div className="bg-white border border-navy/10 rounded-2xl px-5 py-5 my-8 flex flex-wrap items-center gap-4" data-testid="bottom-share-bar">
            <p className="font-dm text-sm font-medium text-navy flex-1 min-w-[160px]">Found this useful? Share it.</p>
            <ShareButtons url={shareUrl} title={article.title} variant="bar" />
          </div>

          {/* Related */}
          {displayRelated.length > 0 && (
            <section className="mt-12 pt-8 border-t border-navy/10" data-testid="related-section">
              <h3 className="font-playfair text-navy text-2xl font-semibold mb-5">Keep reading</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {displayRelated.slice(0, 3).map(r => (
                  <Link key={r.slug} to={`/blog/${r.slug}`} className="bg-white rounded-2xl border border-navy/10 p-4 hover:border-[#00837A] transition-colors block">
                    <p className="font-dm text-[10px] uppercase tracking-[1px] font-medium mb-1.5" style={{ color: "#00837A" }}>{r.category}</p>
                    <p className="font-dm text-navy text-sm font-medium leading-snug line-clamp-3 mb-1.5">{r.title}</p>
                    <p className="font-dm text-xs text-navy/40">{r.read_time || `${Math.max(2, Math.ceil(((r.content || "").split(/\s+/).length) / 200))} min read`}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Comments — only on DB-backed articles, since hardcoded articles have no slug in DB */}
          {dbArticle && <ThreadedComments targetType="blog" targetSlug={dbArticle.slug} />}
        </main>

        {/* SIDEBAR */}
        <aside className="hidden lg:block">
          <div className="sticky top-24 space-y-5">
            {headings.length > 0 && (
              <div className="bg-white border border-navy/10 rounded-2xl p-5" data-testid="toc-box">
                <p className="font-dm text-[11px] uppercase tracking-[1.5px] font-medium text-navy/40 mb-3">In this article</p>
                <ul className="space-y-0">
                  {headings.map(h => (
                    <li key={h.id} className="border-b border-navy/5 last:border-b-0">
                      <a href={`#${h.id}`} data-testid={`toc-link-${h.id}`}
                        onClick={(e) => { e.preventDefault(); const el = document.getElementById(h.id); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                        className={`font-dm text-[13px] py-1.5 block leading-snug transition-colors ${activeHeading === h.id ? "text-navy font-semibold" : "text-navy/60 hover:text-[#00837A]"}`}>
                        {h.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="rounded-2xl p-5" style={{ background: "#0F1E35" }} data-testid="sidebar-cta">
              <p className="font-playfair text-xl font-normal mb-2 text-white">Try <span style={{ color: "#C9A84C" }}>RoleIQ</span> free</p>
              <p className="font-dm text-xs text-white/60 leading-relaxed mb-4">Map competency gaps, run AI coaching sessions, and build future-ready teams — all in one platform.</p>
              <Link to="/generate" className="block text-center font-dm text-sm font-semibold px-4 py-2.5 rounded-lg transition-opacity hover:opacity-90" style={{ background: "#C9A84C", color: "#0F1E35" }}>
                Start free today →
              </Link>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
